/**
 * SVD field types for the storefront buy box. The SVD surface lives on the isolated `SvdProduct` /
 * `SvdProductVariant` domain subclasses (see `SvdProduct.ts` / `SvdProductVariant.ts`) — NOT on the base
 * `Product` / `BaseProductVariant` classes, which are legacy-only.
 *
 * These interfaces mirror the proto contract field-for-field (required/optional per the proto,
 * `@lastbrand/storefront-proto-registry/api/v1/widget`). The `domains/` layer is a proto-free
 * anti-corruption boundary, so we mirror the shapes here rather than import the proto package; the
 * adapter converts proto → domain. Raw dev fixtures that carry extra/legacy fields are cast through
 * `unknown` at their seams (never by widening these types). The only remaining loosely-typed fields are
 * deep gallery / legacy shapes the buy box never reads — each is called out below.
 *
 * The SVD-behaviour types the flow consumes — the `VdaType` render enum and the `SvdSelection`
 * selection state — also live here so every consumer (selector block, shared product form, widget,
 * cascade helper) imports them from one shared place.
 */

/** VDA render category — drives the SVD selector dispatch. Wire value is the raw string. */
export enum VdaType {
  SWATCH = 'SWATCH',
  MEASUREMENT = 'MEASUREMENT',
  SELECT = 'SELECT',
}

/**
 * SVD-native variant selection state — the SVD flow's own selection shape, deliberately kept separate
 * from the legacy `ProductVariantOption` tuple (no adaptation, no mixing). One entry per selected VDA:
 * `vdaKey` matches `SvdVda.key`, `valueKey` matches the chosen `SvdVdaValue.key`.
 */
export interface SvdSelection {
  vdaKey: string
  valueKey: string
}

// Mirrors proto `VdaValue`. Note: sand's MEASUREMENT `sortOrder` is folded into `order` on the V2 wire.
export interface SvdVdaValue {
  key: string // canonical value key — the identity coordinates / selection / URL match on
  urlParamValue?: string // URL-param representation (the `value` half of ?name=value); falls back to `key`
  value: string // raw string form
  localisedValue: string // display-flavored, locale-resolved
  order: number // position within the axis (display + cascade); covers MEASUREMENT ordering too
  hexCode?: string // SWATCH only (HEX_COLOR)
  swatchType?: string // SWATCH only: 'HEX_COLOR' | 'IMAGE'
  swatchImageUrl?: string // SWATCH only (IMAGE) — the field SAND populates for image swatches
  addBorder?: boolean // SWATCH only (HEX_COLOR) — proto `VdaValue.addBorder`; ingest-set border for light dots
  showRedDot?: boolean // every variant under this value carries a promo config
  /**
   * Color group this value belongs to (SWATCH / primary-swatch only) — proto `VdaValue.group`. `value` is
   * the group identity, `localisedValue` its display label. Absent => ungrouped ("core"). Drives the
   * split-into-grouped-rows display (parity with the legacy `colorGrouping` split).
   */
  group?: { value: string; localisedValue: string }
}

// Mirrors proto `VdaDescriptor`.
export interface SvdVda {
  key: string // canonical VDA key: "color", "size", "ringSize" — the identity everything matches on
  urlParamName?: string // URL query-parameter name for deep links; falls back to `key`
  name: string // "Color", "Ring Size" (localised label)
  type: string // VdaType on the wire
  order: number // display + cascade sequence
  isPrimarySwatch: boolean // server-authored primary-swatch marker (proto `VdaDescriptor.isPrimarySwatch`)
  isAnchor: boolean // anchor-VDA marker (proto `VdaDescriptor.isAnchor`)
  isHidden: boolean // hidden-from-picker marker (proto `VdaDescriptor.isHidden`)
  values: SvdVdaValue[]
}

/**
 * Variant-level attribute value — mirrors proto `VariantAttributeValue`. `value` is the canonical value
 * string (matches `VdaValue.key`); the per-variant VDA map (`attributeValues[vdaKey]`) is one legacy
 * coord source the SVD flow can match selections against.
 */
export interface SvdVariantAttributeValue {
  type?: string
  key?: string
  name?: string
  value?: string
  localisedValue?: string
}

/** Declares which attribute keys resolve at product vs variant level — mirrors proto `AttributeMap`. */
export interface SvdAttributeMap {
  product: string[]
  variant: string[]
}

/**
 * Product-level attribute value — mirrors proto `AttributeValue` (polymorphic; `type` discriminates which
 * of the per-case fields is populated). `type` / `name` / `multiValues` are always present (`multiValues`
 * is `[]` for non-MULTI_SELECT). NOTE: the map that holds these (`SvdProduct.attributeValues`) allows
 * `| null` per-entry for raw-SAND tolerance; V2 entries are always non-null.
 */
export interface SvdAttributeValue {
  type: string
  name: string
  key?: string
  value?: string
  localisedValue?: string
  numericValue?: number // NUMERIC
  booleanValue?: boolean // BOOLEAN
  epochTime?: number // DATE
  hexCode?: string // SWATCH
  swatchType?: string // SWATCH
  swatchImageUrl?: string // SWATCH
  imageUrl?: string // IMAGE
  sortOrder?: number // MEASUREMENT
  multiValues: { key: string; value: string; localisedValue: string }[] // MULTI_SELECT ([] otherwise)
}

/** Swatch sibling product's variant — mirrors proto `SwatchVariantV2` (`images` = asset-id refs). */
export interface SvdSwatchVariant {
  id: number
  svid?: number
  sku?: string
  title?: string
  images: string[]
}

/**
 * Swatch sibling product (product-level `swatchProducts[]`) — a separate product the swatch VDA points at
 * (its own id / title / image pool / variants). Mirrors proto `SwatchProduct`.
 */
export interface SvdSwatchProduct {
  id: string
  spid: string
  contentfulProductId?: string
  title?: string
  // proto `{ [assetId]: MediaAsset }` — MediaAsset is a deep gallery shape the buy box never reads
  // (the swatch rail is cross-product navigation), so the value stays loose.
  images: Record<string, unknown>
  variants: SvdSwatchVariant[]
}

// --- V2-native leaf shapes (local mirrors of the proto; keeps the domain layer proto-free) ---

/** Per-variant live availability — mirrors proto `VariantActiveInventory` + `ActiveInventoryTags`. */
export interface SvdActiveInventory {
  available?: number
  inventoryType?: string
  tags?: { preOrderShippingDate?: string }
  isInventoryPresentAtSFWarehouse?: boolean
}

/**
 * Variant media as **asset-id refs** into the gallery pool — mirrors proto `VariantMediaRefs`
 * (`VariantCore.media`): a single ordered `mediaList` plus per-arm experiment groups.
 */
export interface SvdVariantMediaRefs {
  mediaList: string[]
  experimentMediaGroups: {
    // proto `arm: ExperimentArm | undefined` (required key); ExperimentArm's own fields are optional
    arm: { experimentName?: string; variant?: string } | undefined
    mediaList: string[]
  }[]
}

/** Engraving / monogram config — mirrors proto `EngravingConfig` (`data.engravingConfig`). */
export interface SvdEngravingConfig {
  displayName: string
  maxCharacterLimit?: number
  minCharacterLimit?: number
}
